/**
 * Daily check-in dialog (unified: clock-in prompt + mood logger).
 *
 * Opens once per day on the user's first active session. Two sections
 * are rendered independently based on their own conditions:
 *
 *   1. Timecard quick clock-in — shown when timecards are enabled AND no
 *      open shift exists AND no recently-ended shift was already closed
 *      manually by the user (i.e., auto-closed shifts still re-prompt).
 *
 *      Specifically, the "should prompt clock-in" rule is:
 *        enabled=true AND openShift=null
 *        AND NOT (any shift today closed with source='manual')
 *      This means after an idle-timeout or tab-close auto-clock-out the
 *      dialog will re-surface and ask whether to start a new shift.
 *
 *   2. Mood logger — shown when coach is enabled AND moodLogEnabled !== false
 *      AND today's mood hasn't been logged yet.
 *
 * The dialog self-suppresses when neither section applies.
 *
 * Suppressed when:
 *   - Timecard: enabled=false, OR open shift exists, OR last shift was manual
 *   - Mood: coach disabled, moodLogEnabled=false, or already logged today
 *   - Either section, for the rest of the calendar day: the user explicitly
 *     clicked "Skip"/"Skip today", which durably records a per-user/day skip
 *     row server-side (/api/coach/checkin/skip). This is the ONLY thing that
 *     suppresses the whole dialog — logging a mood or clocking in only
 *     suppresses its own section, so the other one can still prompt.
 */
import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import { Badge } from "@/components/ui/badge";
import { Clock, LogIn } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

type CoachSettings = {
  enabled?: boolean;
  moodLogEnabled?: boolean;
};

type TodayMood = { logged: boolean; moodScore?: number };

type TodaySkip = { skipped: boolean };

type TimecardState = {
  enabled: boolean;
  openShift: { id: string; clockInAt: string } | null;
  todayShifts: Array<{ clockOutAt: string | null; clockOutSource?: string | null }>;
};

const SCORES: Array<{ value: number; emoji: string; label: string }> = [
  { value: 1, emoji: "😞", label: "Struggling" },
  { value: 2, emoji: "😕", label: "Off" },
  { value: 3, emoji: "😐", label: "OK" },
  { value: 4, emoji: "🙂", label: "Good" },
  { value: 5, emoji: "😄", label: "Great" },
];

export function MoodLogDialog() {
  const [open, setOpen] = useState(false);
  const [score, setScore] = useState<number | null>(null);
  const [note, setNote] = useState("");
  const { toast } = useToast();
  const qc = useQueryClient();

  // ── Coach mood settings ──────────────────────────────────────────────────
  const { data: settings } = useQuery<CoachSettings>({
    queryKey: ["/api/coach/settings"],
  });
  const { data: today } = useQuery<TodayMood>({
    queryKey: ["/api/coach/mood-log/today"],
    enabled: !!settings?.enabled && settings?.moodLogEnabled !== false,
  });

  // ── Timecard state ────────────────────────────────────────────────────────
  const { data: timecardState } = useQuery<TimecardState>({
    queryKey: ["/api/timecard/me/state"],
  });

  // ── Daily check-in skip state ────────────────────────────────────────────
  // Durable, per-user/day "Skip today"/"Skip" decision. Once skipped, the
  // WHOLE dialog (both sections) stays suppressed for the rest of the
  // calendar day regardless of remounts/refetches/navigation — the same
  // guarantee logging a mood or clocking in already provides for their own
  // section.
  const { data: skipState } = useQuery<TodaySkip>({
    queryKey: ["/api/coach/checkin/skip/today"],
  });
  const skippedToday = skipState?.skipped === true;

  // Whether to show the clock-in prompt:
  //   - timecard is enabled for this user
  //   - no open shift right now (server-authoritative; re-evaluates after idle
  //     auto-close so the prompt re-appears when the user returns)
  //   - user hasn't already manually clocked in+out today
  //     (auto-close sources like idle_timeout, tab_close, midnight, forgot_clockout
  //      do NOT suppress the re-prompt — the user may need to start a new shift)
  //
  // NOTE: No local `clockedIn` state — prompt eligibility is driven entirely by
  // the server shift state.  After clock-in, the query is invalidated, openShift
  // becomes non-null, and showTimecardPrompt becomes false.  After idle clock-out,
  // openShift returns to null and the prompt re-appears on the next dialog open.
  const hasManualClockOutToday = timecardState?.todayShifts?.some(
    (s) => s.clockOutAt && s.clockOutSource === "manual",
  ) ?? false;

  const isShiftOpen = timecardState?.openShift !== null && timecardState?.openShift !== undefined;

  const showTimecardPrompt =
    !skippedToday &&
    timecardState?.enabled === true &&
    !isShiftOpen &&
    !hasManualClockOutToday;

  // Whether to show the mood prompt
  const showMoodPrompt =
    !skippedToday &&
    settings?.enabled === true &&
    settings.moodLogEnabled !== false &&
    today?.logged === false;

  // ── Open dialog once per day when at least one section is active ─────────
  useEffect(() => {
    if (showTimecardPrompt || showMoodPrompt) {
      setOpen(true);
    }
  }, [showTimecardPrompt, showMoodPrompt]);

  // ── Clock-in mutation ─────────────────────────────────────────────────────
  const clockInMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/timecard/me/clock-in"),
    onSuccess: () => {
      // Invalidate server state — openShift will become non-null, which drives
      // showTimecardPrompt to false without any local state needed.
      qc.invalidateQueries({ queryKey: ["/api/timecard/me/state"] });
      // Refresh the manager Shifts list so the new shift shows without reload.
      qc.invalidateQueries({ queryKey: ["/api/admin/timecard/shifts"] });
      toast({ title: "Clocked in", description: "Your shift has started." });
      if (!showMoodPrompt) setOpen(false);
    },
    onError: (err: any) =>
      toast({ title: "Failed to clock in", description: err?.message, variant: "destructive" }),
  });

  // ── Mood save mutation ────────────────────────────────────────────────────
  const save = useMutation({
    mutationFn: async (payload: { moodScore: number; note?: string }) => {
      return apiRequest("POST", "/api/coach/mood-log", payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/coach/mood-log/today"] });
      setOpen(false);
      setScore(null);
      setNote("");
      toast({ title: "Logged", description: "Thanks — that's just for you." });
    },
    onError: () => {
      toast({ title: "Couldn't save", description: "We'll ask again later.", variant: "destructive" });
    },
  });

  // ── Skip mutation ─────────────────────────────────────────────────────────
  // Records the durable per-day skip BEFORE closing the dialog, so
  // showTimecardPrompt/showMoodPrompt flip to false immediately (no reload
  // needed) and the open-triggering effect above never re-fires today.
  const skipMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/coach/checkin/skip"),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/coach/checkin/skip/today"] });
      setOpen(false);
    },
    onError: (err: any) => {
      // Even if the write failed, still close for this session — but don't
      // pretend it's suppressed; just log and let the next remount retry.
      console.error("[daily-checkin] failed to record skip", err);
      setOpen(false);
    },
  });

  const dismiss = () => setOpen(false);
  const skipForToday = () => skipMutation.mutate();

  if (!open) return null;
  if (!showTimecardPrompt && !showMoodPrompt) return null;

  // Minimal clock-in only dialog (no mood section)
  if (showTimecardPrompt && !showMoodPrompt) {
    return (
      <Dialog open={open} onOpenChange={(v) => { if (!v) dismiss(); }}>
        <DialogContent className="sm:max-w-sm" data-testid="dialog-daily-checkin">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-primary" />
              Good morning — clock in?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            You haven't clocked in today. Start your shift now so your hours are tracked.
          </p>
          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={skipForToday}
              disabled={skipMutation.isPending}
              data-testid="button-clockin-skip"
            >
              Skip
            </Button>
            <Button
              className="gap-1.5"
              onClick={() => clockInMutation.mutate()}
              disabled={clockInMutation.isPending}
              data-testid="button-clockin-confirm"
            >
              <LogIn className="w-4 h-4" />
              {clockInMutation.isPending ? "Clocking in…" : "Clock In"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Full unified dialog: mood logger + optional clock-in prompt
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) dismiss(); }}>
      <DialogContent className="sm:max-w-md" data-testid="dialog-daily-checkin">
        <DialogHeader>
          <DialogTitle>Daily check-in</DialogTitle>
        </DialogHeader>

        {/* Clock-in section */}
        {showTimecardPrompt && (
          <div className="flex items-center justify-between rounded-lg border px-4 py-3 bg-muted/30">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-primary shrink-0" />
              <span className="text-sm font-medium">Haven't clocked in yet</span>
            </div>
            <Button
              size="sm"
              className="gap-1.5 shrink-0"
              onClick={() => clockInMutation.mutate()}
              disabled={clockInMutation.isPending || isShiftOpen}
              data-testid="button-unified-clock-in"
            >
              {isShiftOpen ? (
                <Badge variant="outline" className="text-green-600 border-green-500/30 text-xs">Clocked in ✓</Badge>
              ) : (
                <><LogIn className="w-3.5 h-3.5" /> Clock In</>
              )}
            </Button>
          </div>
        )}

        {/* Mood section */}
        {showMoodPrompt && (
          <>
            <p className="text-sm text-muted-foreground -mb-1">How are you doing today?</p>
            <div className="flex justify-between gap-2 py-1">
              {SCORES.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  data-testid={`button-mood-${s.value}`}
                  onClick={() => setScore(s.value)}
                  className={`flex flex-col items-center justify-center rounded-md border p-3 flex-1 transition ${
                    score === s.value
                      ? "border-primary bg-primary/10"
                      : "border-border hover-elevate active-elevate-2"
                  }`}
                >
                  <span className="text-2xl">{s.emoji}</span>
                  <span className="mt-1 text-xs">{s.label}</span>
                </button>
              ))}
            </div>
            <Textarea
              data-testid="input-mood-note"
              placeholder="Anything on your mind? (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={1000}
              rows={3}
            />
          </>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="ghost"
            onClick={skipForToday}
            disabled={skipMutation.isPending}
            data-testid="button-mood-dismiss"
          >
            Skip today
          </Button>
          {showMoodPrompt && (
            <Button
              disabled={score == null || save.isPending}
              onClick={() => score != null && save.mutate({ moodScore: score, note: note.trim() || undefined })}
              data-testid="button-mood-save"
            >
              {save.isPending ? "Saving…" : "Log it"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
